import { useState, useEffect, useCallback } from 'react';
import { attendanceAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Search, Loader2, CheckCircle, XCircle, LogIn, LogOut, Flame, Lock, Delete, User, Send } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import KioskSelfService from './KioskSelfService';
import { useKioskClockFlow } from '../hooks/useKioskClockFlow';

// Helper to get South Africa time (UTC+2)
const getSouthAfricaTime = () => {
  const now = new Date();
  return new Date(now.toLocaleString('en-US', { timeZone: 'Africa/Johannesburg' }));
};

export default function Kiosk() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTime, setCurrentTime] = useState(getSouthAfricaTime());
  const [showSelfService, setShowSelfService] = useState(false);
  const [selfServiceTab, setSelfServiceTab] = useState('profile');
  const { user, logout } = useAuth();

  // Owns the clock-in / PIN / confirmation flow.
  const flow = useKioskClockFlow({ onEmployeesShouldRefresh: () => fetchEmployees() });
  const {
    selectedEmployee, setSelectedEmployee,
    showPinModal, setShowPinModal,
    showActionModal, setShowActionModal,
    showConfirmation, setShowConfirmation,
    confirmationMessage, confirmationWarning,
    pin, setPin,
    pinError, setPinError,
    verifyingPin, verifiedPin,
    processing,
    startFlow,
    handlePinDigit, handlePinBackspace, handlePinClear,
    handleVerifyPin, handleCancelPin,
    handleClockAction, handleCancelAction,
  } = flow;

  // Update clock every second (South Africa time)
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(getSouthAfricaTime()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await attendanceAPI.getEmployeesForKiosk();
      setEmployees(response.data);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
    const interval = setInterval(fetchEmployees, 30000);
    return () => clearInterval(interval);
  }, [fetchEmployees]);

  // Keyboard handler for PIN input
  useEffect(() => {
    if (!showPinModal) return;

    const handleKeyDown = (e) => {
      // Number keys (0-9)
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        setPin(prev => {
          if (prev.length < 6) {
            setPinError('');
            return prev + e.key;
          }
          return prev;
        });
      }
      // Backspace
      else if (e.key === 'Backspace') {
        e.preventDefault();
        setPin(prev => prev.slice(0, -1));
        setPinError('');
      }
      // Escape - cancel
      else if (e.key === 'Escape') {
        e.preventDefault();
        setShowPinModal(false);
        setSelectedEmployee(null);
        setPin('');
        setPinError('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPinModal]);

  // Auto-verify when 6 digits entered via Enter key — delegate to the hook.
  useEffect(() => {
    if (!showPinModal) return;

    const handleEnter = (e) => {
      if (e.key === 'Enter' && pin.length === 6 && !verifyingPin && selectedEmployee) {
        e.preventDefault();
        handleVerifyPin();
      }
    };

    window.addEventListener('keydown', handleEnter);
    return () => window.removeEventListener('keydown', handleEnter);
  }, [showPinModal, pin, verifyingPin, selectedEmployee, handleVerifyPin]);

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setPin('');
    setPinError('');
    setShowPinModal(true);
  };

  const handlePinSubmit = () => {
    if (verifyingPin) return;
    handleVerifyPin();
  };

  const handleOpenSelfService = (initialTab = 'profile') => {
    setSelfServiceTab(initialTab);
    setShowActionModal(false);
    setShowSelfService(true);
  };

  const handleExitSelfService = () => {
    setShowSelfService(false);
    setSelectedEmployee(null);
    setVerifiedPin(null);
    fetchEmployees();
  };

  const filteredEmployees = employees.filter(emp =>
    emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const getStatusColor = (lastAction) => {
    if (lastAction === 'clock_in') return 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400';
    return 'bg-slate-700/50 border-slate-600/50 text-slate-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-xl border-b border-white/5 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
              <Flame className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white"><span className="text-orange-500">Hot</span>forge</h1>
              <p className="text-sm text-slate-400">Time & Attendance</p>
            </div>
          </div>
          
          {/* Clock Display */}
          <div className="text-center">
            <div className="text-5xl font-mono font-bold text-white tracking-wider">
              {currentTime.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div className="text-slate-400 text-lg">
              {currentTime.toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-slate-400">Logged in as</p>
              <p className="text-white font-medium">{user?.full_name}</p>
            </div>
            <Button variant="outline" size="sm" onClick={logout} className="text-slate-400 border-slate-600">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="px-6 py-4">
        <div className="relative max-w-xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400" />
          <Input
            type="text"
            placeholder="Search employee name or number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-14 py-6 text-xl bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
            data-testid="kiosk-search"
          />
        </div>
      </div>

      {/* Employee Grid */}
      <main className="flex-1 overflow-auto px-6 pb-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {filteredEmployees.map((employee) => (
            <button
              key={employee.employee_id}
              onClick={() => handleEmployeeClick(employee)}
              className={cn(
                "p-6 rounded-2xl border-2 transition-all duration-200",
                "hover:scale-105 hover:shadow-xl hover:shadow-orange-500/10",
                "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-slate-900",
                getStatusColor(employee.last_action)
              )}
              data-testid={`employee-card-${employee.employee_id}`}
            >
              <Avatar className="w-20 h-20 mx-auto mb-3 text-2xl">
                <AvatarImage src={employee.avatar} />
                <AvatarFallback className="bg-slate-600 text-white text-xl">
                  {getInitials(employee.full_name)}
                </AvatarFallback>
              </Avatar>
              <p className="text-lg font-semibold text-white text-center truncate">
                {employee.full_name}
              </p>
              <p className="text-sm text-slate-400 text-center truncate">
                {employee.department || 'No Department'}
              </p>
              {employee.last_action && (
                <div className="mt-2 flex items-center justify-center gap-1">
                  {employee.last_action === 'clock_in' ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-emerald-400">In at {employee.last_action_time?.slice(0, 5)}</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-400">Out at {employee.last_action_time?.slice(0, 5)}</span>
                    </>
                  )}
                </div>
              )}
            </button>
          ))}
        </div>
        
        {filteredEmployees.length === 0 && (
          <div className="text-center py-20">
            <p className="text-xl text-slate-400">No employees found</p>
          </div>
        )}
      </main>

      {/* PIN Entry Modal */}
      <Dialog open={showPinModal} onOpenChange={(open) => !open && handleCancelPin()}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white text-center flex items-center justify-center gap-2">
              <Lock className="w-6 h-6 text-orange-500" />
              Enter PIN
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-center text-lg">
              {selectedEmployee?.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6 space-y-6">
            {/* PIN Display */}
            <div className="flex justify-center gap-3">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={cn(
                    "w-12 h-14 rounded-lg border-2 flex items-center justify-center text-2xl font-bold",
                    pin.length > i 
                      ? "border-orange-500 bg-orange-500/20 text-orange-400" 
                      : "border-slate-600 bg-slate-700/50 text-slate-500"
                  )}
                >
                  {pin.length > i ? '●' : ''}
                </div>
              ))}
            </div>

            {/* Error Message */}
            {pinError && (
              <p className="text-red-400 text-center text-sm">{pinError}</p>
            )}

            {/* Number Pad */}
            <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  onClick={() => handlePinDigit(String(num))}
                  className="h-16 text-2xl font-bold rounded-xl bg-slate-700 hover:bg-slate-600 text-white transition-colors"
                  data-testid={`pin-${num}`}
                >
                  {num}
                </button>
              ))}
              <button
                onClick={handlePinClear}
                className="h-16 text-lg font-medium rounded-xl bg-slate-700 hover:bg-slate-600 text-slate-400 transition-colors"
                data-testid="pin-clear"
              >
                Clear
              </button>
              <button
                onClick={() => handlePinDigit('0')}
                className="h-16 text-2xl font-bold rounded-xl bg-slate-700 hover:bg-slate-600 text-white transition-colors"
                data-testid="pin-0"
              >
                0
              </button>
              <button
                onClick={handlePinBackspace}
                className="h-16 rounded-xl bg-slate-700 hover:bg-slate-600 text-slate-400 transition-colors flex items-center justify-center"
                data-testid="pin-backspace"
              >
                <Delete className="w-6 h-6" />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 h-14 text-lg border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={handleCancelPin}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-14 text-lg bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                onClick={handleVerifyPin}
                disabled={pin.length !== 6 || verifyingPin}
                data-testid="verify-pin-btn"
              >
                {verifyingPin ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Verify'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clock In/Out Modal */}
      <Dialog open={showActionModal} onOpenChange={(open) => !open && handleCancelAction()}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white text-center">
              {selectedEmployee?.full_name}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-center text-lg">
              Select your action
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col gap-4 py-6">
            <Button
              size="lg"
              className="h-20 text-2xl bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => handleClockAction('clock_in')}
              disabled={processing}
              data-testid="clock-in-btn"
            >
              {processing ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : (
                <>
                  <LogIn className="w-8 h-8 mr-3" />
                  Clock In
                </>
              )}
            </Button>
            
            <Button
              size="lg"
              className="h-20 text-2xl bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleClockAction('clock_out')}
              disabled={processing}
              data-testid="clock-out-btn"
            >
              {processing ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : (
                <>
                  <LogOut className="w-8 h-8 mr-3" />
                  Clock Out
                </>
              )}
            </Button>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button
                size="lg"
                className="h-16 text-lg bg-slate-700 hover:bg-slate-600 text-white"
                onClick={() => handleOpenSelfService('profile')}
                disabled={processing}
                data-testid="my-profile-btn"
              >
                <User className="w-5 h-5 mr-2" />
                My Profile
              </Button>
              <Button
                size="lg"
                className="h-16 text-lg bg-slate-700 hover:bg-slate-600 text-white"
                onClick={() => handleOpenSelfService('submit')}
                disabled={processing}
                data-testid="submit-leave-btn-kiosk"
              >
                <Send className="w-5 h-5 mr-2" />
                Submit Leave
              </Button>
            </div>

            <Button
              size="lg"
              variant="outline"
              className="h-16 text-xl border-slate-600 text-slate-300 hover:bg-slate-700"
              onClick={handleCancelAction}
              disabled={processing}
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <div className="flex flex-col items-center py-8">
            <div className={cn(
              "w-20 h-20 rounded-full flex items-center justify-center mb-6",
              confirmationWarning ? "bg-amber-500/20" : "bg-emerald-500/20"
            )}>
              {confirmationWarning ? (
                <XCircle className="w-12 h-12 text-amber-400" data-testid="clock-warning-icon" />
              ) : (
                <CheckCircle className="w-12 h-12 text-emerald-500" />
              )}
            </div>
            <p className="text-2xl text-white text-center font-medium" data-testid="clock-confirmation-message">
              {confirmationMessage}
            </p>
            {confirmationWarning && (
              <div
                className="mt-5 px-4 py-3 rounded-md border border-amber-500/60 bg-amber-500/10 text-amber-200 text-sm text-center max-w-sm"
                data-testid="clock-warning-message"
              >
                {confirmationWarning}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Self-Service Panel */}
      {showSelfService && selectedEmployee && verifiedPin && (
        <KioskSelfService
          employee={selectedEmployee}
          pin={verifiedPin}
          initialTab={selfServiceTab}
          onExit={handleExitSelfService}
        />
      )}
    </div>
  );
}
